import type { HeatGridOutcome } from '@repo/front-end-services';
import type { HeatGridWindow } from '@repo/frontend-types';
import { HEAT_VIEW_WORD, heatViewTotal, type HeatView } from './heatView';

/* ── The heat HISTORY (`observer-autogen-heat-history-view`, R13; card 25) ─────────────────────── */

export type GridHeatReadState = 'idle' | 'loading' | HeatGridOutcome;

const WINDOW_DAYS: Record<HeatGridWindow, number> = { '1d': 1, '7d': 7, '14d': 14, '28d': 28 };

export const GRID_HEAT_CAPTIONS = {
  loading: 'Reading heat…',
  failed: 'Couldn’t read heat',
  sizeMismatch: 'recorded at a different frame size',
} as const;

const compactFrames = (n: number): string =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);

/** "last 14 days", or "yesterday" for the one-day window. */
export const windowPhrase = (window: HeatGridWindow): string =>
  window === '1d' ? 'yesterday' : `last ${WINDOW_DAYS[window]} days`;

/** "Feet · 14 days" — the label on the picture while the history is shown. */
export const gridHeatChip = (window: HeatGridWindow, view: HeatView): string =>
  window === '1d'
    ? `${HEAT_VIEW_WORD[view]} · yesterday`
    : `${HEAT_VIEW_WORD[view]} · ${WINDOW_DAYS[window]} days`;

/**
 * The words for a grid read. A FAILED read renders '' here: its sentence is the
 * banner beside Retry. EMPTY is reached only from a ready answer — never from a failure — so "no
 * heat" and "could not read" never look alike (spec criterion 7).
 */
export const gridHeatCaption = (
  state: GridHeatReadState,
  window: HeatGridWindow,
  view: HeatView,
): string => {
  if (state === 'idle') return '';
  if (state === 'loading') return GRID_HEAT_CAPTIONS.loading;
  if (state.status !== 'ready') return '';
  const { coverage, frames, sizeMismatch } = state.value;
  const total = heatViewTotal(state.value, view);
  // Days not recorded yet are said in words, so a window that is only pending never reads as quiet.
  const missing = gridMissingNote(state);
  // Days the server could not bin, and a picture size that changed inside the window, are said too.
  const notes = [
    missing,
    coverage.daysFailed > 0
      ? `${coverage.daysFailed} day${coverage.daysFailed === 1 ? '' : 's'} could not be read`
      : '',
    !sizeMismatch && state.value.warnings.includes('frame-size-changed')
      ? 'recorded at more than one frame size'
      : '',
  ].filter(Boolean);
  if (total === 0 || coverage.daysBinned === 0) {
    const empty =
      window === '1d'
        ? `No ${HEAT_VIEW_WORD[view]} heat recorded for this camera yesterday`
        : `No ${HEAT_VIEW_WORD[view]} heat recorded for this camera in the ${windowPhrase(window)}`;
    return [empty, ...notes].join(' · ');
  }
  const words = [HEAT_VIEW_WORD[view], windowPhrase(window)];
  if (window !== '1d')
    words.push(`${coverage.daysBinned} of ${coverage.daysRequested} days recorded`);
  words.push(`${compactFrames(frames)} frames`);
  if (sizeMismatch) words.push(GRID_HEAT_CAPTIONS.sizeMismatch);
  words.push(...notes);
  return words.join(' · ');
};

/** The Heatmap switch's line for a grid read: the caption, or that the read failed. */
export const gridHeatSubline = (
  state: GridHeatReadState,
  window: HeatGridWindow,
  view: HeatView,
): string =>
  typeof state === 'object' && state.status === 'failed'
    ? GRID_HEAT_CAPTIONS.failed
    : gridHeatCaption(state, window, view);

/** "8 days have no heat yet: 2026-10-04 to 10-11" — named on the picture, never left to look quiet. */
export const gridMissingNote = (state: GridHeatReadState): string => {
  if (typeof state !== 'object' || state.status !== 'ready') return '';
  const missing = [...state.value.coverage.missing].sort();
  if (missing.length === 0) return '';
  if (missing.length === 1) return `1 day has no heat yet: ${missing[0]}`;
  const last = missing[missing.length - 1];
  const lastShort = last.slice(0, 4) === missing[0].slice(0, 4) ? last.slice(5) : last;
  return `${missing.length} days have no heat yet: ${missing[0]} to ${lastShort}`;
};
